import { ACADEMY_QUIZ_OPTIONS } from "../../RentmatchAcademy.constants";
import {
  EAcademyChapter,
  IAcademyScore,
  TAcademyQuizOption,
} from "../../RentmatchAcademy.types";
import {
  getAcademyText,
  getQuizKey,
  getUiKey,
} from "../../RentmatchAcademy.utils";
import { AcademyScore } from "../AcademyScore";
import * as S from "./AcademyQuiz.styles";

// A, B, C: a label like a numbered list, the same in every language, so it is
// not copy for Lokalise.
const LETTER_A_CHAR_CODE = 65;

const getOptionLetter = (option: TAcademyQuizOption) =>
  String.fromCharCode(LETTER_A_CHAR_CODE + option - 1);

interface IAcademyQuizProps {
  chapterId: EAcademyChapter;
  sectionId: string;
  answer: TAcademyQuizOption;
  // The chapter's score so far, shown next to every question.
  score: IAcademyScore;
  // The broker's pick, kept by the chapter so it can count the score.
  selected?: TAcademyQuizOption;
  onSelect: (option: TAcademyQuizOption) => void;
}

// The answer is the lesson: whatever the broker picks, the right option turns
// green and the explanation shows, so even a guess teaches how Rentmatch works.
export const AcademyQuiz = ({
  chapterId,
  sectionId,
  answer,
  score,
  selected,
  onSelect,
}: IAcademyQuizProps) => {
  const getText = (field: string) =>
    getAcademyText(getQuizKey(chapterId, sectionId, field));

  const isAnswered = selected !== undefined;
  const isCorrect = selected === answer;

  // Render
  const renderFeedback = () => (
    <S.Feedback isCorrect={isCorrect} role="status">
      <S.FeedbackTitle>
        {getAcademyText(
          getUiKey(isCorrect ? "quiz.correct" : "quiz.incorrect"),
        )}
      </S.FeedbackTitle>
      {!isCorrect && (
        <div>
          {getAcademyText(getUiKey("quiz.correct_answer"), {
            answer: `${getOptionLetter(answer)}. ${getText(
              `option.${answer}`,
            )}`,
          })}
        </div>
      )}
      <div>{getText("explanation")}</div>
    </S.Feedback>
  );

  return (
    <S.Wrap>
      <S.Header>
        <S.Label>{getAcademyText(getUiKey("quiz.title"))}</S.Label>
        <AcademyScore score={score} />
      </S.Header>
      <S.Question>{getText("question")}</S.Question>
      <S.Options>
        {ACADEMY_QUIZ_OPTIONS.map(option => (
          <S.Option
            key={option}
            type="button"
            disabled={isAnswered}
            isRight={isAnswered && option === answer}
            isWrong={isAnswered && option === selected && option !== answer}
            onClick={() => onSelect(option)}
          >
            <S.OptionLetter aria-hidden={true}>
              {getOptionLetter(option)}
            </S.OptionLetter>
            <span>{getText(`option.${option}`)}</span>
          </S.Option>
        ))}
      </S.Options>
      {isAnswered && renderFeedback()}
    </S.Wrap>
  );
};
